import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AppIcons } from "constants/icons";
import { ParseTableResponse } from "build-ts/chat_session";

// ── Types ──────────────────────────────────────────────────────────────────────

type Step = "paste" | "preview";

export interface UploadTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCsvFileSelected?: (files: File[]) => void;
  /** Parses pasted or uploaded table text — provided by the container. */
  onParseTable: (text: string) => Promise<ParseTableResponse>;
  /**
   * When provided, the modal skips the paste step and auto-parses this file on
   * open — used by the "Upload CSV" file-picker flow so the user sees the same
   * column/type preview before the file is staged in the input chip area.
   */
  initialCsvFile?: File | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Derive a default table name from the first non-empty header, falling back to "table". */
function sanitizeTableName(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** Append last 6 digits of epoch so repeated uploads get distinct artifact names. */
function withUniqueSuffix(sanitizedBase: string): string {
  const base = sanitizedBase || "table";
  return `${base}_${String(Date.now()).slice(-6)}`;
}

function defaultTableName(headers: string[]): string {
  const first = headers.find(h => h.trim().length > 0)?.trim() ?? "table";
  return withUniqueSuffix(sanitizeTableName(first));
}

/** Map a column type label to a Chakra colorScheme for the badge. */
function typeColorScheme(type: string): string {
  switch (type) {
    case "Number":
      return "blue";
    case "Date":
      return "green";
    case "Boolean":
      return "orange";
    default:
      return "gray";
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

const UploadTableModal: React.FC<UploadTableModalProps> = ({
  isOpen,
  onClose,
  onCsvFileSelected,
  onParseTable,
  initialCsvFile,
}) => {
  // ── Step state ───────────────────────────────────────────────────────────────
  const [step, setStep] = useState<Step>("paste");

  // ── Paste step state ─────────────────────────────────────────────────────────
  const [pastedText, setPastedText] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  // ── Preview step state ───────────────────────────────────────────────────────
  const [parsedResponse, setParsedResponse] = useState<ParseTableResponse | null>(null);
  const [tableName, setTableName] = useState("");

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // ── Auto-parse when a CSV file is provided (Upload CSV flow) ─────────────────
  // Fires whenever the modal opens with an initialCsvFile. Reads the file as
  // text and calls parseTable, jumping directly to the preview step so the user
  // sees the same column/type preview they get from the Upload Table flow.
  useEffect(() => {
    if (!isOpen || !initialCsvFile) return;
    let cancelled = false;
    const run = async () => {
      setStep("paste"); // reset in case modal was reused
      setParsedResponse(null);
      setParseError(null);
      setIsParsing(true);
      try {
        const text = await initialCsvFile.text();
        if (cancelled) return;
        const response = await onParseTable(text);
        if (cancelled) return;
        if (!response.success) {
          setParseError(response.error_message || "Could not parse the CSV file.");
          return;
        }
        const stem = initialCsvFile.name.replace(/\.csv$/i, "");
        const base =
          sanitizeTableName(stem) || sanitizeTableName(response.headers.find(h => h.trim().length > 0)?.trim() ?? "");
        setTableName(withUniqueSuffix(base));
        setParsedResponse(response);
        setStep("preview");
      } catch (err: any) {
        if (!cancelled) setParseError(err?.message || "An error occurred while reading the file.");
      } finally {
        if (!cancelled) setIsParsing(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [isOpen, initialCsvFile, onParseTable]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    // Reset all state on close so re-opening starts fresh.
    setStep("paste");
    setPastedText("");
    setIsParsing(false);
    setParseError(null);
    setParsedResponse(null);
    setTableName("");
    onClose();
  }, [onClose]);

  const handleParse = useCallback(async () => {
    if (!pastedText.trim()) {
      setParseError("Please paste a table before clicking Parse Table.");
      return;
    }
    setIsParsing(true);
    setParseError(null);
    try {
      const response = await onParseTable(pastedText);
      if (!response.success) {
        setParseError(response.error_message || "Could not parse the pasted text as a table.");
        return;
      }
      const name = defaultTableName(response.headers);
      setTableName(name);
      setParsedResponse(response);
      setStep("preview");
    } catch (err: any) {
      setParseError(err?.message || "An error occurred while parsing the table.");
    } finally {
      setIsParsing(false);
    }
  }, [pastedText, onParseTable]);

  const handleBack = useCallback(() => {
    setStep("paste");
    setParseError(null);
    setParsedResponse(null);
  }, []);

  const handleUpload = useCallback(() => {
    if (!parsedResponse?.csv_string) return;
    const safeName = sanitizeTableName(tableName.trim() || "table") || "table";
    const file = new File([parsedResponse.csv_string], `${safeName}.csv`, {
      type: "text/csv",
    });
    onCsvFileSelected?.([file]);
    handleClose();
  }, [parsedResponse, tableName, onCsvFileSelected, handleClose]);

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="3xl"
      isCentered
      scrollBehavior="inside"
      returnFocusOnClose={false}
      initialFocusRef={textareaRef}
    >
      <ModalOverlay />
      <ModalContent maxH="85vh">
        {/* ── Header ── */}
        <ModalHeader pb={2}>
          <HStack spacing={3}>
            <Icon as={AppIcons.dataframe} boxSize={6} color="purple.600" />
            <Text m={0} fontSize="lg" fontWeight="600">
              {step === "paste" && !initialCsvFile ? "Paste Table" : "Preview Table"}
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        {/* ── Body ── */}
        <ModalBody px={6} py={4} overflowY="auto">
          {/* Auto-loading state — shown while reading+parsing an initialCsvFile */}
          {isParsing && initialCsvFile ? (
            <VStack spacing={4} py={8} align="center">
              <Spinner size="md" color="purple.500" thickness="3px" />
              <Text fontSize="sm" color="gray.500" m={0}>
                Reading <strong>{initialCsvFile.name}</strong>…
              </Text>
            </VStack>
          ) : parseError && initialCsvFile ? (
            <VStack spacing={3} py={6} align="center">
              <HStack spacing={2} color="red.500">
                <Icon as={AppIcons.warning} boxSize={5} />
                <Text fontSize="sm" fontWeight="600" m={0}>
                  Could not read file
                </Text>
              </HStack>
              <Text fontSize="sm" color="gray.600" textAlign="center" m={0}>
                {parseError}
              </Text>
            </VStack>
          ) : step === "paste" ? (
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600" m={0}>
                Paste your table directly from Excel, Google Sheets, or any CSV. We'll detect the format automatically.
              </Text>
              <Textarea
                ref={textareaRef}
                value={pastedText}
                onChange={e => {
                  setPastedText(e.target.value);
                  if (parseError) setParseError(null);
                }}
                placeholder={[
                  "Paste your table here…",
                  "",
                  "Example:",
                  "date\trevenue\tregion",
                  "2024-01-01\t12000\tNorth",
                  "2024-01-02\t9800\tSouth",
                ].join("\n")}
                minH="220px"
                fontFamily="mono"
                fontSize="sm"
                bg="gray.50"
                borderColor={parseError ? "red.400" : "gray.200"}
                _focus={{
                  borderColor: parseError ? "red.400" : "purple.400",
                  boxShadow: parseError
                    ? "0 0 0 1px var(--chakra-colors-red-400)"
                    : "0 0 0 1px var(--chakra-colors-purple-400)",
                  bg: "white",
                }}
                resize="vertical"
                spellCheck={false}
              />
              {parseError && (
                <HStack spacing={2} color="red.500">
                  <Icon as={AppIcons.warning} boxSize={4} flexShrink={0} />
                  <Text fontSize="sm" m={0}>
                    {parseError}
                  </Text>
                </HStack>
              )}
              <Text fontSize="xs" color="gray.500" m={0}>
                Supports tab-separated (Excel / Google Sheets), comma-separated, and semicolon-separated tables. The
                first row is used as the column header.
              </Text>
            </VStack>
          ) : parsedResponse ? (
            <VStack spacing={5} align="stretch">
              {/* Table name */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={1}>
                  Table name
                </FormLabel>
                <Input
                  value={tableName}
                  onChange={e => setTableName(e.target.value)}
                  size="sm"
                  borderColor="gray.200"
                  _focus={{
                    borderColor: "purple.400",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
                  }}
                  maxW="320px"
                />
                <Text fontSize="xs" color="gray.500" mt={1} mb={0}>
                  This name is how the agent will reference the table.
                </Text>
              </FormControl>

              {/* Stats */}
              <HStack spacing={3}>
                <HStack bg="purple.50" px={3} py={1} borderRadius="md" spacing={1}>
                  <Text fontSize="sm" fontWeight="600" color="purple.700" m={0}>
                    {parsedResponse.headers.length}
                  </Text>
                  <Text fontSize="sm" color="purple.600" m={0}>
                    {parsedResponse.headers.length === 1 ? "column" : "columns"}
                  </Text>
                </HStack>
                <HStack bg="purple.50" px={3} py={1} borderRadius="md" spacing={1}>
                  <Text fontSize="sm" fontWeight="600" color="purple.700" m={0}>
                    {parsedResponse.row_count}
                  </Text>
                  <Text fontSize="sm" color="purple.600" m={0}>
                    {parsedResponse.row_count === 1 ? "row" : "rows"}
                  </Text>
                </HStack>
              </HStack>

              {/* Column types */}
              <Box>
                <Text fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
                  Columns
                </Text>
                <VStack
                  spacing={0}
                  align="stretch"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="gray.200"
                  overflow="hidden"
                >
                  {parsedResponse.headers.map((header, idx) => (
                    <HStack
                      key={`${header}-${idx}`}
                      justify="space-between"
                      px={3}
                      py={2}
                      bg={idx % 2 === 0 ? "white" : "gray.50"}
                      borderBottom={idx < parsedResponse.headers.length - 1 ? "1px solid" : "none"}
                      borderColor="gray.100"
                    >
                      <Text fontSize="sm" fontWeight="500" color="gray.800" m={0} noOfLines={1} flex={1} minW={0}>
                        {header}
                      </Text>
                      <Badge
                        colorScheme={typeColorScheme(parsedResponse.column_types[idx] ?? "Text")}
                        fontSize="xs"
                        flexShrink={0}
                      >
                        {parsedResponse.column_types[idx] ?? "Text"}
                      </Badge>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              {/* Data preview */}
              {parsedResponse.preview_rows.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
                    Preview{" "}
                    <Text as="span" fontWeight="400" color="gray.500">
                      (first {parsedResponse.preview_rows.length} of {parsedResponse.row_count} rows)
                    </Text>
                  </Text>
                  <TableContainer
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    maxH="200px"
                    overflowY="auto"
                  >
                    <Table size="sm" variant="simple">
                      <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                        <Tr>
                          {parsedResponse.headers.map((h, idx) => (
                            <Th
                              key={`th-${idx}`}
                              fontSize="xs"
                              color="gray.600"
                              fontWeight="600"
                              whiteSpace="nowrap"
                              maxW="160px"
                              overflow="hidden"
                              textOverflow="ellipsis"
                            >
                              {h}
                            </Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {parsedResponse.preview_rows.map((row, rIdx) => (
                          <Tr key={`row-${rIdx}`} _hover={{ bg: "gray.50" }}>
                            {row.cells.map((cell, cIdx) => (
                              <Td
                                key={`cell-${rIdx}-${cIdx}`}
                                fontSize="xs"
                                color="gray.700"
                                whiteSpace="nowrap"
                                maxW="160px"
                                overflow="hidden"
                                textOverflow="ellipsis"
                              >
                                {cell}
                              </Td>
                            ))}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </VStack>
          ) : null}
        </ModalBody>

        {/* ── Footer ── */}
        <ModalFooter gap={3} borderTop="1px solid" borderColor="gray.100" pt={3}>
          {isParsing && initialCsvFile ? (
            // While auto-parsing a CSV file, only allow cancelling
            <Button variant="ghost" size="sm" onClick={handleClose}>
              Cancel
            </Button>
          ) : parseError && initialCsvFile ? (
            // Error reading a CSV file — only allow closing
            <Button colorScheme="purple" size="sm" onClick={handleClose}>
              Close
            </Button>
          ) : step === "paste" ? (
            <>
              <Button variant="ghost" onClick={handleClose} size="sm">
                Cancel
              </Button>
              <Button
                colorScheme="purple"
                size="sm"
                onClick={handleParse}
                isLoading={isParsing}
                loadingText="Parsing…"
                isDisabled={!pastedText.trim() || isParsing}
                rightIcon={<Icon as={AppIcons.chevronRight} boxSize={4} />}
              >
                Parse Table
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Icon as={AppIcons.leftArrow} boxSize={4} />}
                onClick={initialCsvFile ? handleClose : handleBack}
              >
                {initialCsvFile ? "Cancel" : "Back"}
              </Button>
              <Button
                colorScheme="purple"
                size="sm"
                leftIcon={<Icon as={AppIcons.upload} boxSize={4} />}
                onClick={handleUpload}
                isDisabled={!tableName.trim()}
              >
                Upload
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UploadTableModal;
